import { ErrorMessage } from 'formik';
import React, { FC, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Editor } from '@tinymce/tinymce-react';

import { DAV_APIS } from '../../../adapters';
import { useTheme } from '../../../context/ThemeContext';

interface CustomEditorProps {
  onChange: (value: any) => void;
  value: any;
  placeholder?: string;
  customClass?: string;
  name?: string;
}

// ─── Editor ───────────────────────────────────────────────────────────────────
const CustomEditor: FC<CustomEditorProps> = ({
  onChange,
  value,
  placeholder,
  customClass,
  name,
}) => {
  const { actualTheme } = useTheme();
  const isDarkMode = actualTheme === 'dark';

  const [isFullscreen, setIsFullscreen] = useState(false);
  const isFullscreenRef = useRef(false);
  const setIsFullscreenRef = useRef(setIsFullscreen);

  const sharedCss = `
    p.drop-cap::first-letter {
      font-size: 3.5em;
      float: left;
      line-height: 0.85;
      margin: 2px 8px -4px 0;
      font-weight: bold;
      font-family: Georgia, serif;
    }
  `;

  const columnBorder = isDarkMode ? '#334155' : '#e2e8f0';
  const columnCss = `
    .col-2 { column-count: 2; column-gap: 28px; column-rule: 1px solid ${columnBorder}; }
    .col-3 { column-count: 3; column-gap: 20px; column-rule: 1px solid ${columnBorder}; }
  `;

  const contentStyle = isDarkMode ?
    `
      html, body { width: 100% !important; height: 100% !important; margin: 0 !important; box-sizing: border-box; }
      body {
        font-family: 'Calibri', 'Segoe UI', Arial, sans-serif;
        font-size: 11pt; line-height: 1.6; color: #e2e8f0;
        background: #1e2d45; padding: 32px 40px !important;
        word-wrap: break-word; overflow-wrap: break-word;
      }
      p { margin: 0 0 8pt 0; }
      h1 { font-size: 20pt; font-weight: 600; color: #f1f5f9; margin: 0 0 12pt 0; }
      h2 { font-size: 16pt; font-weight: 600; color: #e2e8f0; margin: 0 0 10pt 0; }
      h3 { font-size: 13pt; font-weight: 600; color: #cbd5e1; margin: 0 0 8pt 0; }
      table { border-collapse: collapse; width: 100%; margin: 8pt 0; }
      td, th { border: 1px solid #334155; padding: 6pt 8pt; }
      th { background: #0f172a; font-weight: 600; }
      blockquote { border-left: 3px solid #3b82f6; margin: 8pt 0 8pt 16pt; padding-left: 12pt; color: #94a3b8; font-style: italic; }
      a { color: #60a5fa; }
      code { background: #0f172a; padding: 1pt 4pt; border-radius: 3px; font-size: 10pt; }
      pre { background: #0f172a; padding: 12pt; border-radius: 6px; overflow-x: auto; }
      img { max-width: 100%; height: auto; display: block; }
      img[style*="float: left"] { float: left; margin: 4pt 20pt 12pt 0; display: inline; }
      img[style*="float: right"] { float: right; margin: 4pt 0 12pt 20pt; display: inline; }
      figure.image { display: table; }
      figure.image.align-left { float: left; margin: 4pt 20pt 12pt 0; }
      figure.image.align-right { float: right; margin: 4pt 0 12pt 20pt; }
      figure.image figcaption { display: table-caption; caption-side: bottom; font-size: 9pt; color: #94a3b8; text-align: center; padding: 4pt 0 0 0; }
      ${sharedCss}
      ${columnCss}
    `
    : `
      html, body { width: 100% !important; height: 100% !important; margin: 0 !important; box-sizing: border-box; }
      body {
        font-family: 'Calibri', 'Segoe UI', Arial, sans-serif;
        font-size: 11pt; line-height: 1.6; color: #1a202c;
        background: #ffffff; padding: 32px 40px !important;
        word-wrap: break-word; overflow-wrap: break-word;
      }
      p { margin: 0 0 8pt 0; }
      h1 { font-size: 20pt; font-weight: 600; color: #1a202c; margin: 0 0 12pt 0; }
      h2 { font-size: 16pt; font-weight: 600; color: #2d3748; margin: 0 0 10pt 0; }
      h3 { font-size: 13pt; font-weight: 600; color: #4a5568; margin: 0 0 8pt 0; }
      table { border-collapse: collapse; width: 100%; margin: 8pt 0; }
      td, th { border: 1px solid #e2e8f0; padding: 6pt 8pt; }
      th { background: #f7fafc; font-weight: 600; }
      blockquote { border-left: 3px solid #3b82f6; margin: 8pt 0 8pt 16pt; padding-left: 12pt; color: #718096; font-style: italic; }
      a { color: #2563eb; }
      code { background: #f7fafc; padding: 1pt 4pt; border-radius: 3px; font-size: 10pt; border: 1px solid #e2e8f0; }
      pre { background: #f7fafc; padding: 12pt; border-radius: 6px; overflow-x: auto; border: 1px solid #e2e8f0; }
      img { max-width: 100%; height: auto; display: block; }
      img[style*="float: left"] { float: left; margin: 4pt 20pt 12pt 0; display: inline; }
      img[style*="float: right"] { float: right; margin: 4pt 0 12pt 20pt; display: inline; }
      figure.image { display: table; }
      figure.image.align-left { float: left; margin: 4pt 20pt 12pt 0; }
      figure.image.align-right { float: right; margin: 4pt 0 12pt 20pt; }
      figure.image figcaption { display: table-caption; caption-side: bottom; font-size: 9pt; color: #718096; text-align: center; padding: 4pt 0 0 0; }
      ${sharedCss}
      ${columnCss}
    `;

  return (
    <div className={`w-full ${customClass ?? ''}`}>
      <div
        className='tinymce-wrapper overflow-hidden shadow-sm'
        style={{
          background: isDarkMode ? '#0f172a' : '#e5e7eb',
          ...(isFullscreen ?
            {
                position: 'fixed',
                inset: 0,
                zIndex: 99999,
                borderRadius: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }
            : {
                borderRadius: '0.75rem',
              }),
        }}
      >
        {isFullscreen && (
          <style>{`
            .tinymce-wrapper .tox.tox-tinymce {
              flex: 1 !important;
            }
            .tinymce-wrapper .tox-edit-area {
              flex: 1 !important;
            }
            .tinymce-wrapper .tox-edit-area__iframe {
              min-height: calc(100vh - 160px) !important;
              height: 100% !important;
            }
          `}</style>
        )}
        <Editor
          key={actualTheme}
          apiKey='ncioqru4fk9badq949zsvy4hhmfxeir2ikhlxi0l04ycllxk'
          value={value}
          onEditorChange={(content: string) => onChange(content)}
          init={{
            min_height: 600,
            autoresize_bottom_margin: 40,
            menubar: 'file edit view insert format tools table help',
            placeholder: placeholder ?? 'Start writing...',
            plugins:
              'autoresize casechange searchreplace autolink directionality advcode visualblocks visualchars image link media mediaembed codesample table charmap pagebreak nonbreaking anchor tableofcontents insertdatetime advlist lists checklist wordcount editimage help formatpainter permanentpen charmap linkchecker emoticons advtable export print autosave',
            toolbar: [
              'undo redo | customfullscreen | print | formatpainter | blocks fontfamily fontsize',
              'bold italic underline strikethrough | forecolor backcolor highlight | removeformat | superscript subscript | dropcap | charspacing',
              'alignleft aligncenter alignright alignjustify | lineheight outdent indent | bullist numlist checklist | columns',
              'link image table codesample emoticons charmap | pagebreak anchor tableofcontents | zoom | wordcount',
            ],
            toolbar_mode: 'sliding',
            skin: isDarkMode ? 'oxide-dark' : 'oxide',
            content_css: isDarkMode ? 'dark' : 'default',
            content_style: contentStyle,
            branding: false,
            statusbar: true,
            elementpath: false,
            onboarding: false,
            resize: 'vertical',
            formats: {
              letter_spacing_tight: { inline: 'span', styles: { letterSpacing: '-0.5pt' } },
              letter_spacing_normal: { inline: 'span', styles: { letterSpacing: '0pt' } },
              letter_spacing_wide: { inline: 'span', styles: { letterSpacing: '1pt' } },
              letter_spacing_wider: { inline: 'span', styles: { letterSpacing: '2pt' } },
              letter_spacing_widest: { inline: 'span', styles: { letterSpacing: '4pt' } },
            },
            font_family_formats:
              'Calibri=calibri,sans-serif; Arial=arial,helvetica,sans-serif; Times New Roman=times new roman,times; Courier New=courier new,courier,monospace; Georgia=georgia,palatino; Verdana=verdana,geneva;',
            font_size_formats:
              '8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 24pt 28pt 36pt 48pt 72pt',
            line_height_formats: '1 1.15 1.5 2 2.5 3',
            block_formats:
              'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Blockquote=blockquote; Code=code',
            pagebreak_separator:
              '<div class="mce-pagebreak" style="page-break-after: always; border-top: 1px dashed #ccc; margin: 16pt 0;"></div>',
            table_default_styles: { 'border-collapse': 'collapse', width: '100%' },
            image_advtab: true,
            image_caption: true,
            setup: (editor: any) => {
              // ── Register custom icons ────────────────────────────────────────
              // Drop Cap icon — large "A" with text lines beside/below it
              editor.ui.registry.addIcon(
                'drop-cap',
                `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <text x="1" y="19" font-size="20" font-weight="900" font-family="Georgia,serif" fill="currentColor">A</text>
                  <line x1="13" y1="6"  x2="23" y2="6"  stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <line x1="13" y1="10" x2="23" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <line x1="13" y1="14" x2="23" y2="14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <line x1="2"  y1="21" x2="23" y2="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>`,
              );

              editor.ui.registry.addIcon(
                'layout-options',
                `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="2" width="8" height="8" rx="1" fill="currentColor"/>
                  <line x1="13" y1="4" x2="22" y2="4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <line x1="13" y1="7" x2="22" y2="7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <line x1="2" y1="14" x2="22" y2="14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <line x1="2" y1="18" x2="22" y2="18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <line x1="2" y1="21" x2="16" y2="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>`,
              );

              // ── Custom Fullscreen (React wrapper, not TinyMCE plugin) ────────
              editor.ui.registry.addToggleButton('customfullscreen', {
                icon: 'fullscreen',
                tooltip: 'Fullscreen',
                onAction: (api: any) => {
                  const next = !isFullscreenRef.current;
                  isFullscreenRef.current = next;
                  setIsFullscreenRef.current(next);
                  api.setActive(next);
                  document.body.style.overflow = next ? 'hidden' : '';
                },
              });

              // Exit fullscreen on Escape
              editor.on('keydown', (evt: KeyboardEvent) => {
                if (evt.key === 'Escape' && isFullscreenRef.current) {
                  isFullscreenRef.current = false;
                  setIsFullscreenRef.current(false);
                  document.body.style.overflow = '';
                }
              });

              // ── Image context toolbar (replaces image_toolbar config) ────────
              editor.ui.registry.addContextToolbar('imagecontext', {
                predicate: (node: Element) => node.nodeName === 'IMG',
                items: 'layoutoptions | rotateleft rotateright | flipv fliph | editimage',
                position: 'node',
                scope: 'node',
              });

              // ── Image Layout Options ─────────────────────────────────────────
              const applyLayout = (layout: string) => {
                const img = editor.selection.getNode() as HTMLElement;
                if (!img || img.nodeName !== 'IMG') return;
                editor.undoManager.transact(() => {
                  const map: Record<string, Record<string, string>> = {
                    inline: {
                      float: '',
                      display: 'inline',
                      margin: '0 6px 4px 0',
                      'vertical-align': 'middle',
                    },
                    left: {
                      float: 'left',
                      display: 'inline',
                      margin: '0 20px 12px 0',
                      'vertical-align': '',
                    },
                    right: {
                      float: 'right',
                      display: 'inline',
                      margin: '0 0 12px 20px',
                      'vertical-align': '',
                    },
                    center: {
                      float: '',
                      display: 'block',
                      margin: '12px auto',
                      'vertical-align': '',
                    },
                    topleft: {
                      float: 'left',
                      display: 'inline',
                      margin: '0 20px 0 0',
                      'vertical-align': 'top',
                    },
                    topright: {
                      float: 'right',
                      display: 'inline',
                      margin: '0 0 0 20px',
                      'vertical-align': 'top',
                    },
                  };
                  if (map[layout]) editor.dom.setStyles(img, map[layout]);
                });
              };

              editor.ui.registry.addMenuButton('layoutoptions', {
                icon: 'layout-options',
                tooltip: 'Layout Options',
                fetch: (callback: (items: any[]) => void) => {
                  callback([
                    {
                      type: 'menuitem',
                      text: 'Inline with Text',
                      icon: 'sourcecode',
                      onAction: () => applyLayout('inline'),
                    },
                    { type: 'separator' },
                    {
                      type: 'menuitem',
                      text: 'Wrap Left',
                      icon: 'align-left',
                      onAction: () => applyLayout('left'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Wrap Left — Top Aligned',
                      icon: 'align-left',
                      onAction: () => applyLayout('topleft'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Wrap Right',
                      icon: 'align-right',
                      onAction: () => applyLayout('right'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Wrap Right — Top Aligned',
                      icon: 'align-right',
                      onAction: () => applyLayout('topright'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Center — No Wrap',
                      icon: 'align-center',
                      onAction: () => applyLayout('center'),
                    },
                  ]);
                },
              });

              // ── Zoom ────────────────────────────────────────────────────────
              editor.ui.registry.addMenuButton('zoom', {
                icon: 'zoom-in',
                tooltip: 'Zoom',
                fetch: (callback: (items: any[]) => void) => {
                  callback(
                    ['50', '75', '90', '100', '125', '150', '175', '200'].map((level) => ({
                      type: 'menuitem',
                      text: `${level}%`,
                      onAction: () => {
                        const body = editor.getBody() as HTMLElement;
                        (body.style as any).zoom = (parseInt(level, 10) / 100).toString();
                      },
                    })),
                  );
                },
              });

              // ── Drop Cap ────────────────────────────────────────────────────
              editor.ui.registry.addButton('dropcap', {
                icon: 'drop-cap',
                tooltip: 'Drop Cap',
                onAction: () => {
                  const p = editor.dom.getParent(
                    editor.selection.getNode(),
                    'p',
                  ) as HTMLElement | null;
                  if (!p) return;
                  editor.undoManager.transact(() => {
                    if (editor.dom.hasClass(p, 'drop-cap')) {
                      editor.dom.removeClass(p, 'drop-cap');
                    } else {
                      editor.dom.addClass(p, 'drop-cap');
                    }
                  });
                },
              });

              // ── Column Layout ───────────────────────────────────────────────
              editor.ui.registry.addMenuButton('columns', {
                icon: 'table',
                tooltip: 'Column Layout',
                fetch: (callback: (items: any[]) => void) => {
                  callback([
                    {
                      type: 'menuitem',
                      text: 'Single Column',
                      onAction: () => {
                        const wrapper = editor.dom.getParent(
                          editor.selection.getNode(),
                          '.col-2,.col-3',
                        ) as HTMLElement | null;
                        if (wrapper) {
                          editor.dom.removeClass(wrapper, 'col-2');
                          editor.dom.removeClass(wrapper, 'col-3');
                        }
                      },
                    },
                    {
                      type: 'menuitem',
                      text: 'Two Columns',
                      onAction: () =>
                        editor.insertContent(
                          '<div class="col-2"><p>Start writing here...</p></div><p></p>',
                        ),
                    },
                    {
                      type: 'menuitem',
                      text: 'Three Columns',
                      onAction: () =>
                        editor.insertContent(
                          '<div class="col-3"><p>Start writing here...</p></div><p></p>',
                        ),
                    },
                  ]);
                },
              });

              // ── Character Spacing ───────────────────────────────────────────
              editor.ui.registry.addMenuButton('charspacing', {
                icon: 'character-count',
                tooltip: 'Character Spacing',
                fetch: (callback: (items: any[]) => void) => {
                  callback([
                    {
                      type: 'menuitem',
                      text: 'Tight  (-0.5pt)',
                      onAction: () =>
                        editor.execCommand('mceToggleFormat', false, 'letter_spacing_tight'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Normal  (0pt)',
                      onAction: () =>
                        editor.execCommand('mceToggleFormat', false, 'letter_spacing_normal'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Wide  (1pt)',
                      onAction: () =>
                        editor.execCommand('mceToggleFormat', false, 'letter_spacing_wide'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Wider  (2pt)',
                      onAction: () =>
                        editor.execCommand('mceToggleFormat', false, 'letter_spacing_wider'),
                    },
                    {
                      type: 'menuitem',
                      text: 'Widest  (4pt)',
                      onAction: () =>
                        editor.execCommand('mceToggleFormat', false, 'letter_spacing_widest'),
                    },
                  ]);
                },
              });
            },
            automatic_uploads: true,
            file_picker_types: 'image',
            images_upload_handler: async (blobInfo: any) => {
              try {
                const file = blobInfo.blob();
                const res = await DAV_APIS.tour.uploadImage(file as File);
                return res?.data?.url || res?.url || '';
              } catch (error) {
                console.error('Image upload failed:', error);
                throw new Error('Image upload failed');
              }
            },
          }}
        />
      </div>
      {name && (
        <div className='text-sm text-red-500 mt-1 animate-in slide-in-from-top-1 px-1'>
          <ErrorMessage name={name} />
        </div>
      )}
    </div>
  );
};

export default CustomEditor;
